import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { Toast } from '../../components/Toast';
import { useNotifications } from './NotificationContext';

interface ToastContextType {
  showToast: (type: Toast['type'], message: string, saveNotification?: boolean) => void;
  toasts: Toast[];
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const { addNotification } = useNotifications();

  const showToast = useCallback((type: Toast['type'], message: string, saveNotification: boolean = true) => {
    const id = Math.random().toString(36).substr(2, 9);
    setToasts(prev => [...prev, { id, type, message }]);

    // Also save to notifications
    if (saveNotification) {
      addNotification(type, message);
    }
  }, [addNotification]);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ showToast, toasts, removeToast }}>
      {children}
    </ToastContext.Provider>
  );
};

export default ToastContext;
